import React, { useState, useEffect } from "react";
import {
    Box,
    Card,
    CardActionArea,
    Typography,
    ToggleButton,
    ToggleButtonGroup,
    IconButton,
    TextField,
    useTheme,
    useMediaQuery,
} from "@mui/material";
import { ChevronLeft, ChevronRight } from "@mui/icons-material";
import { MAGNETS as magnets } from "./magnetIcons";

export default function MagnetSelector({ magnetType, magnetStrength, onChange }) {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
    const isTablet = useMediaQuery(theme.breakpoints.down("md"));
    const isDark = theme.palette.mode === "dark";

    const [selectedMagnet, setSelectedMagnet] = useState(magnetType || null);
    const [fieldStrength, setFieldStrength] = useState(magnetStrength || "1.5");
    const [customOtherName, setCustomOtherName] = useState("Other");
    const [startIndex, setStartIndex] = useState(0);

    const itemsToShow = isMobile ? 1 : isTablet ? 2 : 3;

    // Sync from props
    useEffect(() => {
        if (magnetType) {
            setSelectedMagnet(magnetType);
        }
        if (magnetStrength) {
            setFieldStrength(magnetStrength);
        }
    }, [magnetType, magnetStrength]);

    // Scroll carousel to show the selected magnet
    useEffect(() => {
        if (selectedMagnet) {
            const magnetIndex = magnets.findIndex(
                (m) => m.id === selectedMagnet,
            );
            if (magnetIndex !== -1) {
                const newStartIndex = Math.max(
                    0,
                    Math.min(magnetIndex, magnets.length - itemsToShow),
                );
                setStartIndex(newStartIndex);
            }
        }
    }, [selectedMagnet, itemsToShow]);

    const handleMagnetSelect = (magnetId) => {
        setSelectedMagnet(magnetId);
        if (magnetId === "GE") {
            onChange({ magnet_type: "GE", magnet_strength: fieldStrength });
        } else if (magnetId === "Other") {
            onChange({
                magnet_type: customOtherName || "Other",
                magnet_strength: "",
            });
        } else {
            onChange({ magnet_type: magnetId, magnet_strength: "" });
        }
    };

    const handleFieldStrengthChange = (event, newStrength) => {
        if (newStrength !== null) {
            setFieldStrength(newStrength);
            onChange({ magnet_type: "GE", magnet_strength: newStrength });
        }
    };

    const handleCustomOtherNameChange = (event) => {
        const newName = event.target.value;
        setCustomOtherName(newName);
        onChange({ magnet_type: newName || "Other", magnet_strength: "" });
    };

    const handlePrevious = () => {
        setStartIndex(Math.max(0, startIndex - 1));
    };

    const handleNext = () => {
        setStartIndex(Math.min(magnets.length - itemsToShow, startIndex + 1));
    };

    const visibleMagnets = magnets.slice(startIndex, startIndex + itemsToShow);
    const canGoPrevious = startIndex > 0;
    const canGoNext = startIndex < magnets.length - itemsToShow;

    return (
        <Box sx={{ width: "100%" }}>
            <Typography variant="body1" gutterBottom sx={{ mb: 2 }}>
                Select Magnet Manufacturer
            </Typography>

            {/* Carousel Container */}
            <Box
                sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: 2,
                    mb: 3,
                }}
            >
                {/* Previous Button */}
                <IconButton
                    onClick={handlePrevious}
                    disabled={!canGoPrevious}
                    sx={{
                        visibility:
                            magnets.length > itemsToShow ? "visible" : "hidden",
                    }}
                >
                    <ChevronLeft />
                </IconButton>

                {/* Magnet Cards */}
                <Box
                    sx={{
                        display: "flex",
                        gap: 2,
                        flex: 1,
                        justifyContent: "center",
                        maxWidth: isMobile ? 300 : isTablet ? 550 : 800,
                    }}
                >
                    {visibleMagnets.map((magnet) => {
                        const IconComponent =
                            isDark && magnet.darkIcon
                                ? magnet.darkIcon
                                : magnet.icon;
                        const isSelected = selectedMagnet === magnet.id;

                        return (
                            <Card
                                key={magnet.id}
                                elevation={0}
                                sx={{
                                    flex: 1,
                                    minWidth: 0,
                                    border: isSelected ? 2 : 0,
                                    borderColor: "primary.main",
                                    transition: "all 0.3s ease",
                                }}
                            >
                                <CardActionArea
                                    onClick={() =>
                                        handleMagnetSelect(magnet.id)
                                    }
                                    sx={{
                                        p: 3,
                                        height: "100%",
                                        display: "flex",
                                        flexDirection: "column",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        minHeight: 180,
                                    }}
                                >
                                    {IconComponent && (
                                        <Box
                                            sx={{
                                                width: "100%",
                                                height: "80px",
                                                display: "flex",
                                                alignItems: "center",
                                                justifyContent: "center",
                                                mb: 2,
                                                "& svg": {
                                                    maxWidth: "100% !important",
                                                    maxHeight:
                                                        "80px !important",
                                                    height: "auto !important",
                                                    width: "auto !important",
                                                },
                                            }}
                                        >
                                            <IconComponent />
                                        </Box>
                                    )}
                                    <Typography
                                        variant="body1"
                                        fontWeight={isSelected ? 600 : 400}
                                        color={
                                            isSelected
                                                ? "primary"
                                                : "textSecondary"
                                        }
                                        align="center"
                                    >
                                        {magnet.name}
                                    </Typography>
                                </CardActionArea>
                            </Card>
                        );
                    })}
                </Box>

                {/* Next Button */}
                <IconButton
                    onClick={handleNext}
                    disabled={!canGoNext}
                    sx={{
                        visibility:
                            magnets.length > itemsToShow ? "visible" : "hidden",
                    }}
                >
                    <ChevronRight />
                </IconButton>
            </Box>

            {/* Field Strength Selector for GE */}
            {selectedMagnet === "GE" && (
                <Box
                    sx={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        mt: 3,
                        p: 2,
                        backgroundColor: theme.palette.action.hover,
                        borderRadius: 2,
                    }}
                >
                    <Typography variant="body1" gutterBottom sx={{ mb: 2 }}>
                        Select Field Strength
                    </Typography>
                    <ToggleButtonGroup
                        value={fieldStrength}
                        exclusive
                        onChange={handleFieldStrengthChange}
                        aria-label="field strength"
                        size={isMobile ? "medium" : "large"}
                    >
                        <ToggleButton
                            value="1.5"
                            aria-label="1.5 tesla"
                            sx={{
                                px: isMobile ? 3 : 4,
                                py: isMobile ? 1 : 1.5,
                                minWidth: isMobile ? 100 : 120,
                            }}
                        >
                            <Typography variant={isMobile ? "body1" : "h6"}>
                                1.5T
                            </Typography>
                        </ToggleButton>
                        <ToggleButton
                            value="3.0"
                            aria-label="3.0 tesla"
                            sx={{
                                px: isMobile ? 3 : 4,
                                py: isMobile ? 1 : 1.5,
                                minWidth: isMobile ? 100 : 120,
                            }}
                        >
                            <Typography variant={isMobile ? "body1" : "h6"}>
                                3.0T
                            </Typography>
                        </ToggleButton>
                    </ToggleButtonGroup>
                </Box>
            )}

            {/* Custom Name Input for Other */}
            {selectedMagnet === "Other" && (
                <Box
                    sx={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        mt: 3,
                        p: 2,
                        backgroundColor: theme.palette.action.hover,
                        borderRadius: 2,
                    }}
                >
                    <Typography variant="body1" gutterBottom sx={{ mb: 2 }}>
                        Enter Custom Magnet Name
                    </Typography>
                    <TextField
                        value={customOtherName}
                        onChange={handleCustomOtherNameChange}
                        placeholder="Other"
                        variant="outlined"
                        size={isMobile ? "small" : "medium"}
                        sx={{
                            width: isMobile ? "100%" : "300px",
                        }}
                        inputProps={{
                            "aria-label": "custom magnet name",
                        }}
                    />
                </Box>
            )}
        </Box>
    );
}
